import type { BrowserOptions } from "@sentry/react"
import {
  browserProfilingIntegration,
  browserTracingIntegration,
  captureConsoleIntegration,
  init,
  replayIntegration,
  setContext,
  setTag,
  setUser,
} from "@sentry/react"
import { auth } from "auth"
import { isFetchError, isValidationError, type FetchError } from "fetcher"
import { never } from "utils"

type Options = {
  isEnabled: boolean
  sentryUrl: string
  environment: string
  release: string
}

export const initializeSentry = async ({
  isEnabled,
  sentryUrl,
  environment,
  release,
}: Options) => {
  if (!isEnabled) return never()

  init({
    dsn: sentryUrl,
    environment: environment,
    release: release,
    integrations: [
      browserTracingIntegration(),
      browserProfilingIntegration(),
      captureConsoleIntegration({ levels: ["error"] }),
      replayIntegration({ maskAllText: true, blockAllMedia: true }),
    ],
    // This will make 100% of all transactions be sent to Sentry.
    tracesSampleRate: 0.1,
    // profile sample rate is relative to the trace sample rate. This in effect will sample profiles for 10% of the sampled traces.
    profilesSampleRate: 0.1, // js-profiling NEEDS TO BE INCLUDED IN THE RESPONSE HEADER FROM THE BACK-END FOR THIS TO WORK
    // Session replay for all sessions where an error occurs
    // At the moment we only use Sentry for all our logs from Portal, and therefore want to all transactions to be present in case we need to debug or look at specific cases.
    // In the future we can use Sentry's "tracesSampler" to be more selective about what is sampled.
    replaysOnErrorSampleRate: 0.5,
    // Set `tracePropagationTargets` to control for which URLs trace propagation should be enabled
    tracePropagationTargets: [
      /^https:\/\/api\.attensi\.com\/api(?!\/v1\/chost\/)/,
      /^https:\/\/auth\.attensi\.com\/api/,
    ],
    beforeSend: onBeforeSend,
  })
}

// This method requires auth to be initialized.
export const setSentryUserContext = async () => {
  const { userId, companyNameKey, subdomain, impersonation } =
    await auth.getActiveSession()

  setUser({ id: String(userId) })

  setTag("company_name_key", companyNameKey)

  setContext("User Session", {
    "Company name key": companyNameKey,
    Subdomain: subdomain,
    Impersonation: impersonation,
  })
}

const onBeforeSend: BrowserOptions["beforeSend"] = (event, hint) => {
  const error = hint.originalException

  if (isFetchError(error)) {
    // Filter out authentication-related errors (401, 403) as they are expected when user is not logged in
    if (error.status === 401 || error.status === 403) {
      return null
    }

    event.level = isValidationError(error) ? "error" : "warning"
    event.fingerprint = getErrorFingerprint(error)
    event.contexts = { ...event.contexts, Request: getErrorContext(error) }
  }

  return event
}

const getErrorContext = (error: FetchError) => ({
  Method: error.method,
  URL: error.url,
  "Scrubbed URL": error.scrubbedUrl,
  "Query qarams": error.params,
  "Status code": error.status,
  "Is online?": error.isOnline,
  "Did request fail?": error.didRequestFail,
})

const getErrorFingerprint = (error: FetchError) => [
  error.name,
  error.message,
  error.method,
  String(error.status),
  error.scrubbedUrl,
]
